import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';
import { http } from '@/api/http';

/**
 * Authenticated user shape as returned by stars-backend.
 *
 * Role enum is narrow on purpose — any future roles must be added here
 * AND in the backend `UserRole` enum in lockstep.
 */
export interface User {
  id: string;
  email: string;
  name?: string;
  telefono?: string;
  role: 'socio' | 'equipo_interno';
}

interface LoginPayload {
  email: string;
  password: string;
}

interface LoginResponse {
  accessToken: string;
  user: User;
}

interface RefreshResponse {
  accessToken: string;
  user: User;
}

interface MeResponse {
  user: User;
}

/**
 * Boot-ready promise — resolved once the initial /auth/refresh completes
 * (success or failure). The router guard awaits this before checking
 * isAuthenticated so a page reload never races to /login.
 */
let _resolveBootReady: () => void;
const _bootReadyPromise = new Promise<void>(r => { _resolveBootReady = r; });

/**
 * Auth store — holds the access token in-memory only.
 *
 * Security notes:
 * - Access token is NEVER persisted to localStorage / sessionStorage.
 *   Persisting it would expose it to XSS. On reload we rely on the
 *   httpOnly refresh cookie + /auth/refresh to re-issue a token.
 * - The refresh token itself lives exclusively in the httpOnly cookie
 *   set by the backend; JS never sees it.
 */
export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(null);
  const user = ref<User | null>(null);
  const isRefreshing = ref(false);

  const isAuthenticated = computed<boolean>(() => !!accessToken.value && !!user.value);
  const isSocio = computed<boolean>(() => user.value?.role === 'socio');

  function setSession(token: string, u: User): void {
    accessToken.value = token;
    user.value = u;
  }

  function clear(): void {
    accessToken.value = null;
    user.value = null;
  }

  /**
   * POST /auth/login
   * Throws a Spanish user-friendly message on failure.
   */
  async function login({ email, password }: LoginPayload): Promise<User> {
    try {
      const { data } = await http.post<LoginResponse>('/auth/login', {
        email: email.trim().toLowerCase(),
        password,
      });
      setSession(data.accessToken, data.user);
      return data.user;
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 401) {
        throw new Error('Credenciales inválidas');
      }
      if (status === 429) {
        throw new Error('Demasiados intentos, esperá unos minutos');
      }
      throw new Error('No se pudo iniciar sesión. Intentá de nuevo.');
    }
  }

  /**
   * POST /auth/refresh — reads httpOnly refresh cookie, returns a new
   * access token AND the current user payload.
   *
   * We MUST hydrate both atomically:
   *  - setting only the token while `user` is still null leaves
   *    `isAuthenticated` false, which causes guards to bounce users
   *    to /login between the refresh resolving and a follow-up
   *    /auth/me arriving (the original B1 boot race).
   *  - discarding `user` from the response means stale role/name
   *    survive until the user re-logs in (the B2 contract mismatch).
   *
   * Clears the store on any failure so callers know the session is
   * dead.
   */
  async function refreshAccessToken(): Promise<void> {
    isRefreshing.value = true;
    try {
      const { data } = await http.post<RefreshResponse>('/auth/refresh', undefined, {
        // Tag the request so the response interceptor skips it.
        headers: { 'X-Skip-Auth-Retry': '1' },
      });
      setSession(data.accessToken, data.user);
    } catch (err) {
      clear();
      throw err;
    } finally {
      isRefreshing.value = false;
    }
  }

  /**
   * GET /auth/me — re-hydrates user from a valid access token.
   * Used on app boot after a successful cookie-based refresh.
   */
  async function fetchMe(): Promise<User> {
    const { data } = await http.get<MeResponse>('/auth/me');
    user.value = data.user;
    return data.user;
  }

  /**
   * POST /auth/logout — fire-and-forget. We clear the local store even
   * if the server call fails (network, 5xx). The refresh cookie will
   * either be cleared by the server or expire naturally.
   *
   * We log every failure at `warn` so operators can see logout issues
   * in the browser console, but never rethrow — logout must be
   * idempotent from the UI's perspective.
   */
  async function logout(): Promise<void> {
    try {
      await http.post('/auth/logout', undefined, {
        headers: { 'X-Skip-Auth-Retry': '1' },
      });
    } catch (err) {
      // Previously this guard was inverted (`!axios.isAxiosError(err)`),
      // which silenced the expected failure mode (network / 5xx,
      // which ARE axios errors) and only logged the impossible case.
      // Just log every failure.
      console.warn('logout call failed', err);
    } finally {
      clear();
    }
  }

  function notifyBootReady(): void { _resolveBootReady(); }
  function waitForBoot(): Promise<void> { return _bootReadyPromise; }

  return {
    // state
    accessToken,
    user,
    isRefreshing,
    // getters
    isAuthenticated,
    isSocio,
    // actions
    setSession,
    clear,
    login,
    refreshAccessToken,
    fetchMe,
    logout,
    notifyBootReady,
    waitForBoot,
  };
});
